'use client';

import { useEffect, useState } from 'react';
import { useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';

/**
 * Video feed hook for the Tube module's {@link VideoCarousel} block.
 *
 * <p>Backed by the canonical {@code MediaSource} adapter
 * ({@code media.videos.fetchSource}) — the host mounts an adapter that
 * proxies the Cinema8 Tube backend ({@code /v1/public/{tenantId}/tube/*}).
 * No adapter mounted → empty (the carousel renders its EmptyState).
 */

export interface TubeVideo {
  id: string;
  slug?: string;
  title: string;
  thumbnail?: string;
  duration?: string;
}

export interface UseTubeVideosOpts {
  source?: string;
  limit?: number;
}

export interface UseTubeVideosResult {
  videos: TubeVideo[];
  loading: boolean;
}

function formatDuration(seconds: number | undefined): string | undefined {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return undefined;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

function toTubeVideo(item: MediaItem): TubeVideo {
  return {
    id: item.id,
    slug: item.slug,
    title: item.title ?? '',
    thumbnail: item.imageUrl,
    duration: formatDuration(item.durationSeconds),
  };
}

export function useTubeVideos(opts: UseTubeVideosOpts = {}): UseTubeVideosResult {
  const { source = 'featured', limit = 8 } = opts;
  const media = useMediaSource();
  const [state, setState] = useState<UseTubeVideosResult>({ videos: [], loading: Boolean(media) });

  useEffect(() => {
    if (!media) {
      setState({ videos: [], loading: false });
      return;
    }
    const controller = new AbortController();
    setState({ videos: [], loading: true });

    (async () => {
      try {
        const res = await media.videos.fetchSource({ source, limit, signal: controller.signal });
        if (controller.signal.aborted) return;
        setState({ videos: (res?.items ?? []).map(toTubeVideo), loading: false });
      } catch (e) {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setState({ videos: [], loading: false });
      }
    })();

    return () => controller.abort();
  }, [media, source, limit]);

  return state;
}
